// ===========================================
// Parent Resources Page
// ===========================================
// Parent view of shared files and links (students have no resources page).
// Reads ONLY family-scoped endpoints: /google-drive/files, /class-links and
// /users/me/children. Never call a staff-gated read here (/google-drive/auth/status,
// /students, /lessons): each 403s for a parent (#493).

import { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  Skeleton,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import FileDownloadCard from '../../components/googleDrive/FileDownloadCard';
import LinkCard from '../../components/classLinks/LinkCard';
import { useClassLinks } from '../../hooks/useClassLinks';
import { ClassLink } from '../../api/classLinks.api';
import { useGoogleDriveFiles } from '../../hooks/useGoogleDrive';
import { useMyChildren } from '../../hooks/useParentProfile';
import { GoogleDriveFile, FilesQuery } from '../../api/googleDrive.api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

// ===========================================
// GROUPING
// ===========================================

/**
 * One heading rule for links and files, in the order the "New File Shared"
 * notification uses: class, then the child's own folder, then the level, then Other.
 */
function groupLabel(source: {
  Lesson?: { name: string } | null;
  Student?: { firstName: string } | null;
  ProgramLevel?: { name: string } | null;
}): string {
  if (source.Lesson?.name) return source.Lesson.name;
  if (source.Student?.firstName) return `${source.Student.firstName}'s files`;
  if (source.ProgramLevel?.name) return `${source.ProgramLevel.name} (all classes)`;
  return 'Other Resources';
}

// ===========================================
// COMPONENT
// ===========================================

export default function ResourcesPage() {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChild, setSelectedChild] = useState<string>('');

  // Debounce search
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Queries
  const { data: children, isLoading: isLoadingChildren } = useMyChildren();

  // Build filters
  const filters: FilesQuery = useMemo(() => {
    const f: FilesQuery = {};
    if (selectedChild) f.studentId = selectedChild;
    return f;
  }, [selectedChild]);

  // No "Drive connected" signal: the server returns exactly what this family may read (#493).
  const { data: files, isLoading: isLoadingFiles, error } = useGoogleDriveFiles(filters);
  const {
    data: links,
    isLoading: isLoadingLinks,
    isError: linksError,
  } = useClassLinks({
    ...(selectedChild ? { studentId: selectedChild } : {}),
  });

  // Links grouped by lesson name (a program-level link lands under its level's name).
  const filteredLinks = useMemo(() => {
    if (!links) return [];
    // The server scopes a parent to their family's enrolled classes (and a chosen child).
    if (!debouncedSearch) return links;
    const q = debouncedSearch.toLowerCase();
    return links.filter(
      (l) =>
        l.title.toLowerCase().includes(q) ||
        (l.description ?? '').toLowerCase().includes(q) ||
        (l.Lesson?.name ?? '').toLowerCase().includes(q) ||
        (l.ProgramLevel?.name ?? '').toLowerCase().includes(q)
    );
  }, [links, debouncedSearch]);
  const linksByLesson = useMemo(() => {
    const grouped: Record<string, ClassLink[]> = {};
    for (const link of filteredLinks) {
      (grouped[groupLabel(link)] ??= []).push(link);
    }
    return grouped;
  }, [filteredLinks]);

  // Filter files by search query (client-side)
  const filteredFiles = useMemo(() => {
    if (!files) return [];
    if (!debouncedSearch) return files;

    const query = debouncedSearch.toLowerCase();
    return files.filter((file) =>
      file.fileName.toLowerCase().includes(query) ||
      file.GoogleDriveFolder?.Lesson?.name.toLowerCase().includes(query) ||
      file.GoogleDriveFolder?.ProgramLevel?.name.toLowerCase().includes(query) ||
      file.GoogleDriveFolder?.Student?.firstName.toLowerCase().includes(query) ||
      file.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  }, [files, debouncedSearch]);

  // Group files by lesson
  const filesByLesson = useMemo(() => {
    const grouped: Record<string, GoogleDriveFile[]> = {};

    filteredFiles.forEach((file) => {
      // `GoogleDriveFolder.Lesson` as the API sends it. This read `file.folder?.lesson`
      // (never sent), so EVERY file landed in "Other Resources" (#202).
      const lessonName = groupLabel(file.GoogleDriveFolder ?? {});
      if (!grouped[lessonName]) {
        grouped[lessonName] = [];
      }
      grouped[lessonName].push(file);
    });

    return grouped;
  }, [filteredFiles]);

  // Handle child selection
  const handleChildChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedChild(newValue);
  };

  // Loading state
  if (isLoadingChildren) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={40} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Box>
    );
  }

  const isLoadingAny = isLoadingFiles || isLoadingLinks;
  const totalCount = filteredFiles.length + filteredLinks.length;
  const lessonNames = Array.from(new Set([...Object.keys(linksByLesson), ...Object.keys(filesByLesson)]));

  return (
    <Box>
      <PageHeader
        title="Resources"
        subtitle="View and download lesson materials and resources"
      />

      {/* Child Selector Tabs (if multiple children) */}
      {children && children.length > 1 && (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={selectedChild}
            onChange={handleChildChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="All Children" value="" />
            {children.map((student) => (
              <Tab
                key={student.id}
                label={`${student.firstName} ${student.lastName}`}
                value={student.id}
              />
            ))}
          </Tabs>
        </Paper>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search resources..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 250 } }}
        />
      </Box>

      {/* Error state */}
      {(error || linksError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load resources. Please try again later.
        </Alert>
      )}

      {/* Loading state */}
      {isLoadingAny && (
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Skeleton variant="rectangular" height={240} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Results count */}
      {!isLoadingAny && !error && !linksError && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {totalCount} resource{totalCount !== 1 ? 's' : ''} available
          {debouncedSearch && ` matching "${debouncedSearch}"`}
        </Typography>
      )}

      {/* Empty state — only when BOTH lists have loaded empty, never on an error */}
      {!isLoadingAny && !error && !linksError && totalCount === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <FolderIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Resources Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {debouncedSearch
              ? 'Try a different search term'
              : 'Your teacher will upload resources here as they become available'}
          </Typography>
        </Paper>
      )}

      {/* Links + files grouped by lesson (links first) */}
      {!isLoadingAny && totalCount > 0 && (
        <Box>
          {lessonNames.map((lessonName) => (
            <Box key={lessonName} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {lessonName}
              </Typography>
              <Grid container spacing={2}>
                {(linksByLesson[lessonName] ?? []).map((link) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={link.id}>
                    <LinkCard link={link} showProgramLevel={false} />
                  </Grid>
                ))}
                {(filesByLesson[lessonName] ?? []).map((file) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                    <FileDownloadCard file={file} showLesson={false} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
